declare global {
  interface Window {
    Sentry: typeof import('@sentry/browser')
  }
}

const { error, warn, log } = console
const { Sentry } = window

if (Sentry) {
  // Adapted from https://stackoverflow.com/a/53214615/890
  console.error = reportingConsoleError // defined via function hoisting
  console.warn = reportingConsoleWarn // defined via function hoisting
}

export function logException(
  exception: unknown,
  tags?: Record<string, string>
) {
  if (Sentry) {
    Sentry.withScope(scope => {
      if (tags) {
        for (const tag of Object.keys(tags)) {
          scope.setTag(tag, tags[tag])
        }
      }

      Sentry.captureException(exception)
    })
  }
  error(exception)
}

export function logMessage(message: string, tags?: Record<string, string>) {
  if (Sentry) {
    Sentry.withScope(scope => {
      if (tags) {
        for (const tag of Object.keys(tags)) {
          scope.setTag(tag, tags[tag])
        }
      }

      Sentry.captureMessage(message)
    })
  }
  log(message)
}

function reportingConsoleError(...args: any[]) {
  const argsCopy = [...args]
  Sentry.captureException(reduceConsoleArgs(argsCopy), { level: 'error' })
  return error.apply(console, argsCopy)
}

function reportingConsoleWarn(...args: any[]) {
  const argsCopy = [...args]
  Sentry.captureMessage(reduceConsoleArgs(argsCopy), { level: 'warning' })
  return warn.apply(console, argsCopy)
}

function reduceConsoleArgs(args: any[]) {
  let errorMsg = args[0]
  // Make sure errorMsg is either an error or string.
  // It's therefore best to pass in new Error('msg') instead of just 'msg' since
  // that'll give you a stack trace leading up to the creation of that new Error
  // whereas if you just pass in a plain string 'msg', the stack trace will include
  // reportingConsoleError and reportingConsoleCall
  if (!(errorMsg instanceof Error)) {
    // stringify all args as a new Error (which creates a stack trace)
    errorMsg = new Error(
      args.reduce(function (accumulator, currentValue) {
        let s = currentValue.toString()
        if (s === '[object Object]') {
          s = JSON.stringify(currentValue)
        }
        return accumulator.toString() + ' ' + currentValue.toString()
      }, '')
    )
  }
  return errorMsg
}
